import { el } from "./dom";
import type { PhotoEntry } from "../content/photos.generated";

export interface SectionCoverOpts {
  eyebrow?: string;
  title: string;
  photo: PhotoEntry | null;
  /** CSS background-position for the bg image; defaults to "center". */
  bgPosition?: string;
}

/**
 * A 100vh "cover" with a full-bleed image background and the section title
 * overlaid. Renders as the first child of each section so as the user scrolls
 * down, each section announces itself with a dramatic image + title moment
 * before its content begins on the cream bg.
 *
 * Title text is wrapped in per-line clip spans so each line can mask-reveal
 * from below as the cover enters the viewport. The bg image is also panned
 * via a CSS Ken Burns animation defined in global.css.
 */
export function sectionCover(opts: SectionCoverOpts): HTMLElement {
  const cover = el("div", { class: "section-cover" });

  if (opts.photo) {
    const folder = opts.photo.folder;
    const base = opts.photo.base;
    const widths = opts.photo.widths;

    const small = widths.find((w) => w >= 480) ?? widths[0] ?? 1280;
    const mid = widths.find((w) => w >= 1280) ?? small;
    const large = widths[widths.length - 1] ?? mid;

    const url = (w: number, ext: string) => `/img/${folder}/${base}-${w}.${ext}`;

    // Use image-set so phones get small variant and retina/desktop get large.
    const set = (ext: string) =>
      `image-set(url("${url(small, ext)}") 1x, url("${url(mid, ext)}") 2x, url("${url(large, ext)}") 3x)`;

    // Mark cover as having a bg-image (CSS uses --section-cover-bg in ::after for Ken Burns)
    cover.setAttribute("data-bg-image", "1");
    cover.style.setProperty(
      "--section-cover-bg",
      `${set("avif")}, ${set("webp")}, url("${url(mid, "jpg")}")`,
    );
    cover.style.setProperty("--section-cover-lqip", `url(${opts.photo.lqip})`);
    if (opts.bgPosition) {
      cover.style.setProperty("--section-cover-position", opts.bgPosition);
    }
  }

  // Optional eyebrow with inner span for translateY reveal
  if (opts.eyebrow) {
    const eyebrow = el("p", { class: "section-cover__eyebrow" });
    eyebrow.appendChild(el("span", { class: "section-cover__eyebrow-inner" }, opts.eyebrow));
    cover.appendChild(eyebrow);
  }

  // Title wrapped per-line: split on visual whitespace into <span class="line"><span>word(s)</span></span>
  const title = el("h2", { class: "section-cover__title" });
  const lines = splitIntoLines(opts.title);
  for (const line of lines) {
    const lineNode = el("span", { class: "section-cover__title-line" });
    lineNode.appendChild(el("span", {}, line));
    title.appendChild(lineNode);
  }
  cover.appendChild(title);

  // IntersectionObserver triggers the reveal once the cover scrolls into view
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          cover.classList.add("is-revealed");
          io.disconnect();
        }
      }
    },
    { threshold: 0.18 },
  );
  // Defer observation until the element is actually in the DOM
  requestAnimationFrame(() => io.observe(cover));

  return cover;
}

/**
 * Naive line splitter for the cover title.
 *  - 1 word              → keep on one line
 *  - short title (<= 16 chars total) → one line, let it breathe
 *  - 2 words long        → break between
 *  - 3 words             → first word alone, last two together (subject / predicate)
 *  - 4+ words            → roughly equal halves
 */
function splitIntoLines(text: string): string[] {
  const trimmed = text.trim();
  const words = trimmed.split(/\s+/);
  if (words.length <= 1) return [trimmed];
  // Short-to-medium titles stay on one line. 20 chars covers things like
  // "Vrae en Antwoorde" (17) and "Ons Groot Dag" (13).
  if (trimmed.length <= 20) return [trimmed];
  if (words.length === 2) return words;
  if (words.length === 3) return [words[0]!, words.slice(1).join(" ")];
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
}
